import React, {Component} from 'react';
import styled from 'styled-components';

import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../helpers/src/interaction/interactionHelpers';
import {makeInputId} from '../../../../helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../styles/commonStyles';
import {appearanceNone} from '../../styles/mixins';
import {PaletteColorsEnum} from '../../styles/paletteModel';
import {makeSizeConstants} from '../../styles/styleHelpers';
import {boxShadow} from '../../theme/themeStyleHelpers';
import {UserTheme} from '../../theme/themeTypes';
import {ThemeContextType} from '../../theme/userThemeProvider';
import {BooleanInputProps} from './booleanInputTypes';

/*
 * Props.
 */

export interface ToggleProps extends BooleanInputProps, InteractionComponentProps {
  /** The size variant to render. */
  size?: VisualSizesEnum;
  shouldPreventClick?: boolean;
}

/*
 * Style.
 */

const sizeVariants = makeSizeConstants(
  {
    width: '30px',
    height: '20px',
    padding: '3px',
    knobSize: '14px',
    knobTranslation: '10px',
    findKnobShadow: (theme: UserTheme) =>
      `0 0 1px ${theme.alphas.black20}, 0 1px 3px ${theme.alphas.black40}`,
  },
  {
    width: '50px',
    height: '30px',
    padding: '4px',
    knobSize: '22px',
    knobTranslation: '20px',
    findKnobShadow: (theme: UserTheme) =>
      `0 0 2px ${theme.alphas.black20}, 0 2px 5px ${theme.alphas.black40}`,
  },
);

interface ToggleStyleProps {
  $size: VisualSizesEnum;
  $isChecked?: boolean;
  $isDisabled?: boolean;
}
const StyledWrapperLabel = styled.label<ToggleStyleProps>`
  display: block;
  padding: ${(p) => sizeVariants[p.$size].padding};

  width: ${(p) => sizeVariants[p.$size].width};
  height: ${(p) => sizeVariants[p.$size].height};
  border-radius: 16px;

  background-color: ${(p) => findWrapperBackgroundColor(p)};
  transition: background-color 100ms ease;
`;

function findWrapperBackgroundColor(props: ToggleStyleProps & ThemeContextType) {
  // Normal state.
  if (!props.$isChecked) {
    return props.theme.greys.shade40;
  }

  // Disabled state.
  if (props.$isDisabled) {
    return props.theme.palette[PaletteColorsEnum.BLUE].shade30;
  }

  // Checked state.
  return props.theme.palette[PaletteColorsEnum.BLUE].shade40;
}

const StyledKnobInput = styled.input<ToggleStyleProps>`
  display: block;
  ${appearanceNone()};

  width: ${(p) => sizeVariants[p.$size].knobSize};
  height: ${(p) => sizeVariants[p.$size].knobSize};
  border-radius: 50%;
  background-color: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, sizeVariants[p.$size].findKnobShadow(p.theme))};

  transition: transform 300ms ease;
  transform: translateX(${(p) => (p.$isChecked ? sizeVariants[p.$size].knobTranslation : '0')});
`;

/*
 * Component.
 */

/** iOS-like toggle input. */
export class Toggle extends Component<ToggleProps> {
  constructor(props: ToggleProps) {
    super(props);
    this.inputId = makeInputId('toggle-input');
  }

  private readonly inputId: string;

  render() {
    const {isChecked, isDisabled, onChange, size = VisualSizesEnum.MEDIUM} = this.props;
    return (
      <StyledWrapperLabel
        $size={size}
        $isChecked={this.props.isChecked}
        $isDisabled={this.props.isDisabled}
        htmlFor={this.inputId}
      >
        <StyledKnobInput
          $size={size}
          $isChecked={this.props.isChecked}
          $isDisabled={this.props.isDisabled}
          onClick={(e) => {
            if (this.props.shouldPreventClick) {
              e.preventDefault();
            }
          }}
          id={this.inputId}
          type="checkbox"
          checked={isChecked}
          disabled={isDisabled}
          onChange={() => onChange(!isChecked)}
          {...buildInteractionIdProps(this.props.interactionId)}
        />
      </StyledWrapperLabel>
    );
  }
}
